/**
 * ガイドの価格表ページ（/guide/price-payment/<スラッグ>/。WordPress の guide の投稿で、add_js に price-payment を指定）
 * MEMO: 旧 js/price-payment.js・price-payment_getItemPriceApi.js の ESM 版
 *       ページごとの価格表の定義は price-pages.ts
 *       旧版の不具合を修正:
 *         - 封筒の発送日の切替えで、価格が 1 つ返るたびに表を作り直し、前の発送日の価格が混ざった表やローダーの非表示が先に出ていた（すべて揃ってから 1 回だけ作る）
 *         - 区分の見出し行の td が tr の外（tbody の直下）に置かれていた
 *         - PC 用の表の行を、モバイル用の表にも空の行として追加していた（チラシ・はがき・封筒）
 *         - 列・行のハイライトが、ページ表示時にあった td にしか効かず、後から作る価格表で効いていなかった
 */
import { onReady, queryAll } from '@/utils/dom'
import { getAppEnv } from '@/utils/env'
import { fadeIn, fadeOut, hide, show } from '@/utils/fade'
import { fetchPrices } from '@/utils/price'
import type { PriceRow } from '@/utils/price'
import { PRICE_PAGES } from './price-pages'
import type { PricePage, PriceSection } from './price-pages'

//モバイル表示で PC 用・モバイル用の価格表を切り替えるウィンドウ幅
const TABLE_SWITCH_WIDTH = 640
//ウィンドウサイズ変更から価格表の切替えまでの待ち時間
const RESIZE_DELAY = 100
//価格表を作ってから表示するまでの待ち時間（旧版と同じ）
const SHOW_DELAY = 500
//モバイル用の 1 つ目の表に入れる列の数（残りの列は 2 つ目の表）
const MOBILE_FIRST_COLUMNS = 3
//区分の見出し行の色
const SECTION_HEAD_STYLE = 'background-color: #8b8b8b; color: white; font-weight: bold; border-right: 0;'
//マスのハイライトの色（列と行・マウスが乗っているマス）
const HOVER_LINE_COLOR = '#dbfff7'
const HOVER_CELL_COLOR = '#9dfeea'

const WRAP = '#price-payment-price_table_wrap'
const PC_WRAP = '#price-payment-price_table_pc-wrap'
const MOBILE_WRAP = '#price-payment-price_table_mobile-wrap'
const LOADER = '#price-payment-price_tabel_load'
const DAY_SELECT = '[name="price-payment-envelope_day"]'

//PC 用・モバイル用の表と、表ごとに入れる列
const TABLES: { selector: string, getColumns: (count: number) => number[] }[] = [
    { selector: `${PC_WRAP} tbody`, getColumns: (count) => [...Array(count).keys()] },
    { selector: '#price-payment-price_table_mobile-01 tbody', getColumns: (count) => [...Array(Math.min(count, MOBILE_FIRST_COLUMNS)).keys()] },
    { selector: '#price-payment-price_table_mobile-02 tbody', getColumns: (count) => [...Array(Math.max(count - MOBILE_FIRST_COLUMNS, 0)).keys()].map((i) => i + MOBILE_FIRST_COLUMNS) },
]

//区分ごとの、列ごとの価格
interface SectionPrices {
    section: PriceSection
    columns: PriceRow[][]
}

const isPc = (): boolean => getAppEnv().ua==='other'
const getElement = (selector: string): HTMLElement | null => document.querySelector<HTMLElement>(selector)

/**
 * 発送日の選択（封筒）の選択可否を切り替える
 */
const setDaySelectDisabled = (disabled: boolean): void => {
    queryAll<HTMLInputElement>(DAY_SELECT).forEach((input) => {
        input.disabled = disabled
        input.classList.toggle('is-disabled', disabled)
        input.closest('.j-form_swtch_item')?.classList.toggle('is-disabled', disabled)
    })
}

/**
 * 価格表の包含要素の高さを、表示中の価格表に合わせる
 */
const adjustWrapHeight = (): void => {
    const wrap = getElement(WRAP)
    const usePc = isPc() || window.innerWidth>TABLE_SWITCH_WIDTH
    const table = getElement(usePc ? PC_WRAP : MOBILE_WRAP)
    if(wrap && table) wrap.style.height = `${table.offsetHeight}px`
}

/**
 * 価格表ローダーの表示（発送日の選択を操作不可にする）。表示が完了したら解決する Promise を返す
 */
const showLoader = (): Promise<void> => {
    const loader = getElement(LOADER)
    setDaySelectDisabled(true)
    return loader ? fadeIn(loader) : Promise.resolve()
}

/**
 * 価格表ローダーの非表示（発送日の選択を操作可能に戻し、包含要素の高さを合わせる）
 */
const hideLoader = (): void => {
    setDaySelectDisabled(false)
    const loader = getElement(LOADER)
    if(loader) fadeOut(loader)
    adjustWrapHeight()
}

/**
 * PC 以外の端末で、ウィンドウ幅に応じて PC 用・モバイル用の価格表を出し分ける
 */
const switchTableType = (): void => {
    if(isPc()) return
    const pcWrap = getElement(PC_WRAP)
    const mobileWrap = getElement(MOBILE_WRAP)
    if(window.innerWidth>TABLE_SWITCH_WIDTH) {
        if(mobileWrap) hide(mobileWrap)
        if(pcWrap) fadeIn(pcWrap)
    }
    else {
        if(pcWrap) hide(pcWrap)
        if(document.querySelector('.price-color_table_swtch')) document.querySelector<HTMLElement>('.j-color_type_swtch')?.click()
        if(mobileWrap) fadeIn(mobileWrap)
    }
}

/**
 * 区分ごとに、列ごとの価格を取得する（1 つでも失敗した場合は null）
 */
const fetchSectionPrices = async (page: PricePage, sections: PriceSection[]): Promise<SectionPrices[] | null> => {
    const results = await Promise.all(sections.map(async (section) => {
        const columns = await Promise.all(section.columns.map((optionSets) => fetchPrices(page.endPoint, {
            itemId: section.itemId,
            maxLotAmount: section.maxLotAmount,
            optionSets,
        })))
        return columns.every((rows) => rows) ? { section, columns: columns as PriceRow[][] } : null
    }))
    return results.every((result) => result) ? results as SectionPrices[] : null
}

/**
 * 1 つの表に、区分の見出し行と価格の行を書き出す
 */
const renderTable = (tbody: HTMLElement, sectionPrices: SectionPrices[], columnIndexes: number[]): void => {
    tbody.replaceChildren()
    sectionPrices.forEach(({ section, columns }) => {
        if(section.label) {
            const head = document.createElement('tr')
            const cell = document.createElement('td')
            cell.colSpan = columnIndexes.length + 1
            cell.className = 'js-price_table-title'
            cell.style.cssText = SECTION_HEAD_STYLE
            cell.textContent = section.label
            head.append(cell)
            tbody.append(head)
        }
        columns[0].forEach((row, index) => {
            if(section.rowFilter && !section.rowFilter(index)) return
            const tr = document.createElement('tr')
            const th = document.createElement('th')
            th.innerHTML = section.rowHeader(row, index)
            tr.append(th)
            columnIndexes.forEach((column) => {
                const td = document.createElement('td')
                td.textContent = `￥${columns[column][index].totalPriceWithTax.toLocaleString()}`
                tr.append(td)
            })
            tbody.append(tr)
        })
    })
}

//価格表の作り直しの回数（取得中に発送日が変わった場合、古い取得の結果は使わない）
let loadCount = 0

/**
 * 価格を取得して、PC 用・モバイル用の価格表を作り直す
 */
const loadPriceTable = async (page: PricePage, day: string): Promise<void> => {
    const current = ++loadCount
    const loaderShown = showLoader()
    const sectionPrices = await fetchSectionPrices(page, page.getSections(day))
    if(!sectionPrices || current!==loadCount) return
  //ローダーの表示が完了してから書き換える（待っている間に次の依頼が始まった場合は書き換えない）
    await loaderShown
    if(current!==loadCount) return
    const columnCount = sectionPrices[0].columns.length
    TABLES.forEach(({ selector, getColumns }) => {
        const tbody = getElement(selector)
        const columnIndexes = getColumns(columnCount)
        if(tbody && columnIndexes.length) renderTable(tbody, sectionPrices, columnIndexes)
    })
    switchTableType()
    setTimeout(() => {
        const wrap = getElement(WRAP)
        if(wrap) show(wrap)
        hideLoader()
    }, SHOW_DELAY)
}

/**
 * 価格表のマスにマウスが乗ったら、列と行を含めて背景色を変える
 *   MEMO: 価格表は書き換えられるため、document で受けて対象のマスを判定する（mouseenter・mouseleave 相当）
 */
const initPriceCellHover = (): void => {
    let current: HTMLTableCellElement | null = null
    const setHighlight = (cell: HTMLTableCellElement, on: boolean): void => {
        const column = cell.cellIndex + 1
        cell.closest('table.p-table_price')?.querySelectorAll<HTMLElement>(`td:nth-child(${column})`).forEach((td) => {
            td.style.backgroundColor = on ? HOVER_LINE_COLOR : ''
        })
        Array.from(cell.parentElement?.children ?? []).forEach((elm) => {
            (elm as HTMLElement).style.backgroundColor = on ? HOVER_LINE_COLOR : ''
        })
        cell.style.backgroundColor = on ? HOVER_CELL_COLOR : ''
    }
    document.addEventListener('mouseover', (event) => {
        const cell = (event.target as Element).closest<HTMLTableCellElement>('.j-table_hover td:not(.js-price_table-title)')
        if(cell===current) return
        if(current) setHighlight(current, false)
        current = cell
        if(cell) setHighlight(cell, true)
    })
}

/**
 * PC 以外の端末の、モバイル用の表の切替えボタンと、ウィンドウサイズ変更での価格表の切替え
 */
const initMobileTableSwitch = (): void => {
    if(isPc()) return
    const buttons = queryAll<HTMLElement>('.j-color_type_swtch')
    buttons.forEach((button) => {
        button.addEventListener('click', () => {
            buttons.forEach((elm) => elm.classList.remove('c-btn-org'))
            button.classList.add('c-btn-org')
            buttons.forEach((elm) => {
                const table = document.getElementById(elm.dataset.targetTable ?? '')
                if(table) hide(table)
            })
            const target = document.getElementById(button.dataset.targetTable ?? '')
            if(target) fadeIn(target)
        })
    })
    let timer = 0
    window.addEventListener('resize', () => {
        clearTimeout(timer)
        timer = window.setTimeout(() => {
            adjustWrapHeight()
            switchTableType()
        }, RESIZE_DELAY)
    })
}

onReady(() => {
    initPriceCellHover()
    initMobileTableSwitch()
    const page = PRICE_PAGES[document.body.id]
    if(!page) return
    const getDay = (): string => document.querySelector<HTMLInputElement>(`${DAY_SELECT}:checked`)?.value ?? ''
    loadPriceTable(page, getDay())
  //封筒の発送日の切替え
    if(page.hasDaySelect) {
        queryAll<HTMLInputElement>(DAY_SELECT).forEach((input) => {
            input.addEventListener('change', () => loadPriceTable(page, getDay()))
        })
    }
})
